import { api } from './api';
import type {
  WordPressConnectionStatus, WordPressTerm, WordPressPublishResult,
  WebhookConfigView, WebhookDelivery, WebhookEvent,
  McpTokenView, McpTokenScope,
  ApiTokenView,
} from '@traaker/shared';

export const wordpressApi = {
  status: (brandId: string) =>
    api.get<WordPressConnectionStatus>(`/integrations/wordpress/status?brandId=${brandId}`),

  connect: (body: { brandId: string; siteUrl: string; username: string; applicationPassword: string }) =>
    api.post<WordPressConnectionStatus>('/integrations/wordpress/connect', body),

  disconnect: (brandId: string) =>
    api.delete<{ ok: boolean }>(`/integrations/wordpress/connect?brandId=${brandId}`),

  test: (brandId: string) =>
    api.post<{ ok: boolean }>('/integrations/wordpress/test', { brandId }),

  categories: (brandId: string) =>
    api.get<WordPressTerm[]>(`/integrations/wordpress/categories?brandId=${brandId}`),

  publish: (body: {
    brandId: string;
    title: string;
    articleMarkdown: string;
    status: 'draft' | 'publish';
    categoryIds?: number[];
  }) => api.post<WordPressPublishResult>('/integrations/wordpress/publish', body),
};

export const feedsApi = {
  url: (brandId: string) => api.get<{ url: string }>(`/feeds/url?brandId=${brandId}`),
};

export const webhooksApi = {
  list: () => api.get<WebhookConfigView[]>('/integrations/webhooks'),
  create: (body: { url: string; events?: WebhookEvent[] }) =>
    api.post<WebhookConfigView & { secret: string }>('/integrations/webhooks', body),
  update: (id: string, body: { active?: boolean; events?: WebhookEvent[] }) =>
    api.patch<WebhookConfigView>(`/integrations/webhooks/${id}`, body),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/integrations/webhooks/${id}`),
  test: (id: string) => api.post<{ ok: boolean }>(`/integrations/webhooks/${id}/test`, {}),
  deliveries: (id: string) => api.get<WebhookDelivery[]>(`/integrations/webhooks/${id}/deliveries`),
};

export const mcpTokensApi = {
  list: () => api.get<McpTokenView[]>('/integrations/mcp-tokens'),
  create: (body: { name?: string; scope?: McpTokenScope }) =>
    api.post<McpTokenView & { token: string }>('/integrations/mcp-tokens', body),
  revoke: (id: string) => api.delete<{ ok: boolean }>(`/integrations/mcp-tokens/${id}`),
};

/** Public REST API keys (`trk_api_…`) — a separate credential kind from the MCP
 *  tokens above, on separate routes, so either can be rotated alone. No `scope`:
 *  /api/public/v1 is read-only. */
export const apiTokensApi = {
  list: () => api.get<ApiTokenView[]>('/integrations/api-tokens'),
  create: (body: { name?: string }) =>
    api.post<ApiTokenView & { token: string }>('/integrations/api-tokens', body),
  revoke: (id: string) => api.delete<{ ok: boolean }>(`/integrations/api-tokens/${id}`),
};
